import { useCallback, useEffect, useState } from "react";
import { Loader2, Mail, MessageCircle, Phone, Search, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

const STATUSES = ["New", "Contacted", "Confirmed", "Completed"];

const STATUS_STYLES = {
  New: "bg-rose/10 text-rose",
  Contacted: "bg-amber-100 text-amber-700",
  Confirmed: "bg-emerald-100 text-emerald-700",
  Completed: "bg-neutral-200 text-neutral-600",
};

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export default function AdminEnquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(async (s, st) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/enquiries", {
        params: { search: s, status: st },
      });
      setItems(data);
    } catch {
      setError("Could not load enquiries. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(search, status), 300);
    return () => clearTimeout(t);
  }, [search, status, load]);

  const changeStatus = async (item, next) => {
    const prev = item.status;
    setItems((list) =>
      list.map((e) => (e.id === item.id ? { ...e, status: next } : e)),
    );
    try {
      await api.patch(`/admin/enquiries/${item.id}`, { status: next });
    } catch {
      setItems((list) =>
        list.map((e) => (e.id === item.id ? { ...e, status: prev } : e)),
      );
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete the enquiry from ${item.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/enquiries/${item.id}`);
      setItems((list) => list.filter((e) => e.id !== item.id));
    } catch {
      setError("Could not delete the enquiry. Please try again.");
    }
  };

  return (
    <section data-testid="admin-enquiries">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 sm:max-w-sm">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
          <input
            data-testid="enquiry-search-input"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, phone, email, gift…"
            className="w-full rounded-full border border-line bg-white py-3 pl-11 pr-4 text-sm outline-none transition-colors focus:border-rose"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {["", ...STATUSES].map((s) => (
            <button
              key={s || "all"}
              data-testid={`enquiry-filter-${s ? s.toLowerCase() : "all"}`}
              onClick={() => setStatus(s)}
              className={`rounded-full border px-4 py-2 text-xs font-medium transition-colors ${
                status === s
                  ? "border-ink bg-ink text-cream"
                  : "border-line bg-white text-[#5C5C5C] hover:border-rose hover:text-rose"
              }`}
            >
              {s || "All"}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <p data-testid="enquiries-error" className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-xs font-medium text-red-600">
          {error}
        </p>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 size={26} className="animate-spin text-rose" />
        </div>
      ) : items.length === 0 ? (
        <p data-testid="enquiries-empty" className="rounded-2xl border border-line bg-white py-16 text-center text-sm text-[#5C5C5C]">
          No enquiries found.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {items.map((e) => (
            <article
              key={e.id}
              data-testid={`enquiry-row-${e.id}`}
              className="rounded-2xl border border-line bg-white p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-serif text-xl font-medium">{e.name}</h3>
                  <p className="mt-0.5 text-xs text-[#5C5C5C]">{formatDate(e.created_at)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    data-testid={`enquiry-status-${e.id}`}
                    value={e.status || "New"}
                    onChange={(ev) => changeStatus(e, ev.target.value)}
                    aria-label={`Status for enquiry from ${e.name}`}
                    className={`cursor-pointer rounded-full border-0 px-3.5 py-2 text-xs font-semibold outline-none ${STATUS_STYLES[e.status || "New"]}`}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  <button
                    data-testid={`enquiry-delete-${e.id}`}
                    onClick={() => remove(e)}
                    aria-label={`Delete enquiry from ${e.name}`}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-[#5C5C5C] transition-colors hover:border-red-400 hover:text-red-500"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>

              <div className="mt-4 grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
                <a href={`tel:+${e.phone}`} className="inline-flex items-center gap-2 text-ink transition-colors hover:text-rose">
                  <Phone size={13} className="text-rose" /> {e.phone}
                </a>
                {e.email && (
                  <a href={`mailto:${e.email}`} className="inline-flex items-center gap-2 text-ink transition-colors hover:text-rose">
                    <Mail size={13} className="text-rose" /> {e.email}
                  </a>
                )}
                <a
                  href={`https://wa.me/${e.phone.replace(/\D/g, "").length === 10 ? "91" : ""}${e.phone.replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-ink transition-colors hover:text-rose"
                >
                  <MessageCircle size={13} className="text-rose" /> WhatsApp
                </a>
                {e.occasion && <p className="text-[#5C5C5C]">Occasion: <span className="text-ink">{e.occasion}</span></p>}
                {e.gift && <p className="text-[#5C5C5C]">Gift: <span className="text-ink">{e.gift}</span></p>}
                {e.budget && <p className="text-[#5C5C5C]">Budget: <span className="text-ink">{e.budget}</span></p>}
              </div>

              {(e.customization || e.message) && (
                <div className="mt-4 flex flex-col gap-2 border-t border-line pt-4 text-sm">
                  {e.customization && (
                    <p className="text-[#5C5C5C]"><span className="font-semibold text-ink">Customization:</span> {e.customization}</p>
                  )}
                  {e.message && (
                    <p className="text-[#5C5C5C]"><span className="font-semibold text-ink">Message:</span> {e.message}</p>
                  )}
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
